"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { useSelector } from "react-redux"
import { useWallet } from "@solana/wallet-adapter-react"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { api } from "@/lib/api"
import { Connection, PublicKey } from "@solana/web3.js"
import { RefreshCw, Info, Loader2 } from "lucide-react"
import { approveAutoRenewalDelegation } from "@/lib/auto-renewal"
import { PriceDisplay } from "@/components/price-display"
import { RootState } from "@/lib/store"

interface SubscriptionFormProps {
  onSubscribe: (platformId: string, irmaAmount: number, durationDays: number, autoRenew: boolean) => Promise<void>
  connection?: Connection
  irmaMint?: string
}

export function SubscriptionForm({ onSubscribe, connection, irmaMint }: SubscriptionFormProps) {
  const { publicKey, signTransaction } = useWallet()
  const storedPlatformCode = useSelector((state: RootState) => state.platform.platformCode)
  const storedEmail = useSelector((state: RootState) => state.platform.emailId)
  const storedUsdAmount = useSelector((state: RootState) => state.platform.usdAmount)

  const [loading, setLoading] = useState(false)
  const [platformCode, setPlatformCode] = useState<string | null>(null)
  const [platformName, setPlatformName] = useState<string | null>(null)
  const [platformLoading, setPlatformLoading] = useState(true)
  const [usdAmount, setUsdAmount] = useState("")
  const [irmaTokens, setIrmaTokens] = useState<number | null>(null)
  const [convertingPrice, setConvertingPrice] = useState(false)
  const [error, setError] = useState("")
  const [irmaBalance, setIrmaBalance] = useState<number>(0)
  const [autoRenew, setAutoRenew] = useState(false)
  const [approvingDelegation, setApprovingDelegation] = useState(false)
  const [userEmail, setUserEmail] = useState<string | null>(null)
  const [insufficientBalance, setInsufficientBalance] = useState(false)

  useEffect(() => {
    if (storedEmail) setUserEmail(storedEmail)
    if (storedUsdAmount) setUsdAmount(storedUsdAmount)
    if (storedPlatformCode) {
      setPlatformCode(storedPlatformCode)
      api.getBackToPlatformUrl(storedPlatformCode).then((res) => {
        if (res.platform_name) setPlatformName(res.platform_name)
        setPlatformLoading(false)
      }).catch(() => setPlatformLoading(false))
    } else {
      setPlatformLoading(false)
    }
  }, [storedPlatformCode, storedEmail, storedUsdAmount])

  useEffect(() => {
    const usd = parseFloat(usdAmount)
    if (!usd || usd < 1) { setIrmaTokens(null); return }
    setConvertingPrice(true)
    api.getTokenExchangeValue(usd).then((res) => {
      setIrmaTokens(res.tokens)
      setConvertingPrice(false)
    }).catch(() => setConvertingPrice(false))
  }, [usdAmount])

  useEffect(() => {
    if (publicKey && connection && irmaMint && irmaMint !== "11111111111111111111111111111111") {
      loadIrmaBalance()
    }
  }, [publicKey, connection, irmaMint])

  useEffect(() => {
    if (irmaTokens !== null && irmaBalance > 0) {
      setInsufficientBalance(irmaBalance < irmaTokens)
    } else {
      setInsufficientBalance(false)
    }
  }, [irmaTokens, irmaBalance])

  const loadIrmaBalance = async () => {
    if (!publicKey || !connection || !irmaMint) return
    try {
      const { getAssociatedTokenAddress, getAccount } = await import("@solana/spl-token")
      const mint = new PublicKey(irmaMint)
      const ata = await getAssociatedTokenAddress(mint, publicKey)
      const account = await getAccount(connection, ata)
      setIrmaBalance(Number(account.amount) / 1e9)
    } catch (err) {
      setIrmaBalance(0)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")
    if (!publicKey) { setError("Please connect your wallet"); return }
    if (!platformCode) { setError("Platform not detected. Please access this page from your platform."); return }
    const usd = parseFloat(usdAmount)
    if (!usd || usd < 1) { setError("Please enter an amount of $1 or more"); return }
    if (!connection || !irmaMint || irmaMint === "11111111111111111111111111111111") {
      setError("Connection or IRMA mint not configured"); return
    }
    try {
      setLoading(true)
      const exchangeResult = await api.getTokenExchangeValue(usd)
      const irmaAmount = exchangeResult.tokens
      if (irmaBalance < irmaAmount) {
        setError("Insufficient IRMA balance. Please use the Swap tab to buy IRMA tokens first.")
        setLoading(false); return
      }
      const durationDays = 30
      if (autoRenew) {
        try {
          setApprovingDelegation(true)
          const mint = new PublicKey(irmaMint)
          const priceInSmallestUnit = Math.floor(irmaAmount * 1_000_000_000)
          if (!signTransaction) throw new Error("Wallet signTransaction not available")
          await approveAutoRenewalDelegation(connection, publicKey, mint, priceInSmallestUnit, { publicKey, signTransaction })
          console.log("Auto-renewal delegation approved")
        } catch (delegationError: any) {
          console.error("Delegation approval failed:", delegationError)
          setError(`Failed to approve auto-renewal: ${delegationError.message}`)
          setApprovingDelegation(false); setLoading(false); return
        } finally {
          setApprovingDelegation(false)
        }
      }
      await onSubscribe(platformCode, irmaAmount, durationDays, autoRenew)
      setAutoRenew(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Subscription failed")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="w-full max-w-[480px] mx-auto">
      <div className="bg-[#13161C] rounded-3xl p-3 border border-white/5 shadow-2xl">
        <div className="px-1 mb-3">
          <div className="text-lg font-bold text-white mb-1">Subscribe</div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          {/* Wallet */}
          <div className="bg-[#1C1F26] rounded-2xl p-4 border border-transparent">
            <label className="block text-sm text-[#8B949E] font-medium mb-2 ml-1">Connected Wallet</label>
            <div className="flex justify-between items-center">
              <span className="text-[#8B949E] text-sm">
                {publicKey ? publicKey.toBase58().slice(0, 8) + "..." : "Not connected"}
              </span>
              {publicKey && (
                <div className="flex items-center gap-2">
                  <span className="font-bold text-white">{irmaBalance.toFixed(4)} IRMA</span>
                  <button type="button" onClick={loadIrmaBalance} disabled={loading || !publicKey}
                    className="text-[#8B949E] hover:text-white transition-colors" title="Refresh balance">
                    <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* User Email */}
          {userEmail && (
            <div className="bg-[#1C1F26] rounded-2xl p-4 border border-transparent">
              <label className="block text-sm text-[#8B949E] font-medium mb-2 ml-1">User</label>
              <span className="text-[#8B949E] text-sm">{userEmail}</span>
            </div>
          )}

          {/* Platform */}
          <div className="bg-[#1C1F26] rounded-2xl p-4 border border-transparent">
            <label className="block text-sm text-[#8B949E] font-medium mb-2 ml-1">Platform</label>
            {platformLoading ? (
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-[#8B949E]" />
                <span className="text-[#8B949E] text-sm">Detecting platform...</span>
              </div>
            ) : platformCode ? (
              <div>
                <p className="font-bold text-lg text-white">{platformName || platformCode}</p>
                {platformName && platformName !== platformCode && (
                  <p className="text-xs text-[#8B949E]">Code: {platformCode}</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-red-400">No platform detected. Please access this page from your platform link.</p>
            )}
          </div>

          {/* USD Amount */}
          <div className="bg-[#1C1F26] rounded-2xl p-4 pb-5 border border-transparent focus-within:border-white/10 transition">
            <label className="block text-sm text-[#8B949E] font-medium mb-3 ml-1">Subscription Fee (USD)</label>
            <input
              type="number"
              value={usdAmount}
              onChange={(e) => setUsdAmount(e.target.value)}
              placeholder="0.00"
              min="1"
              step="0.01"
              disabled={!!storedUsdAmount}
              className="w-full bg-transparent text-right text-[32px] font-semibold outline-none text-white placeholder-[#3b4153] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none disabled:opacity-60"
            />
            {convertingPrice && (
              <p className="text-xs text-[#8B949E] flex items-center gap-1 mt-1 justify-end">
                <Loader2 className="h-3 w-3 animate-spin" /> Converting to IRMA...
              </p>
            )}
            {irmaTokens !== null && !convertingPrice && (
              <p className="text-xs text-[#8B949E] mt-1 text-right">≈ {irmaTokens.toFixed(4)} IRMA tokens</p>
            )}
          </div>

          {/* Insufficient balance warning */}
          {insufficientBalance && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 text-sm text-red-400">
              Insufficient IRMA balance ({irmaBalance.toFixed(4)} IRMA). Please use the Swap tab to buy more IRMA tokens.
            </div>
          )}

          {/* Dynamic Price Display */}
          {connection && usdAmount && parseFloat(usdAmount) > 0 && (
            <PriceDisplay usdAmount={parseFloat(usdAmount)} />
          )}

          {/* Auto-Renewal */}
          <div className="bg-[#1C1F26] rounded-2xl p-4 border border-transparent">
            <div className="flex items-start space-x-3">
              <Checkbox
                id="auto-renew"
                checked={autoRenew}
                onCheckedChange={(checked) => setAutoRenew(checked === true)}
                disabled={loading || approvingDelegation}
                className="mt-1"
              />
              <div className="flex-1 space-y-1">
                <Label htmlFor="auto-renew" className="text-sm font-medium text-white cursor-pointer">
                  Enable Auto-Renewal
                </Label>
                <p className="text-xs text-[#8B949E]">
                  Automatically renew your subscription when it expires. You'll need to approve token delegation (one-time).
                </p>
                {autoRenew && (
                  <div className="flex items-start gap-2 mt-2 p-2 bg-blue-500/10 rounded-lg text-xs">
                    <Info className="h-3 w-3 mt-0.5 text-blue-400 shrink-0" />
                    <p className="text-blue-300">
                      Auto-renewal will automatically charge your wallet when the subscription expires. You can disable it anytime.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 text-sm text-red-400">{error}</div>
          )}

          <div className="mt-3">
            <button
              type="submit"
              disabled={loading || approvingDelegation || !publicKey || !platformCode || !usdAmount || insufficientBalance}
              className="w-full bg-[#25e4e8] hover:bg-[#1dd1d5] disabled:bg-[#8B949E] disabled:cursor-not-allowed text-black font-bold text-[17px] py-4 rounded-xl transition active:scale-[0.98] disabled:active:scale-100"
            >
              {approvingDelegation ? "Approving Auto-Renewal..." : loading ? "Processing..." : "Subscribe Now"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
